import { MutableRefObject, useCallback } from 'react';

import type {
  ColDef,
  Column,
  ColumnApi,
  ExcelStyle,
  GridApi,
  ValueFormatterParams,
} from 'ag-grid-community';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { saveAs } from 'file-saver';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

const TEXT_COLUMN_NAMES = new Set([
  'sirereg',
  'donorreg',
  'regnumber',
  'registrationnumber',
]);

const isTextColumn = (header: string) =>
  TEXT_COLUMN_NAMES.has(header.toLowerCase().replace(/\s+/g, ''));

type CellExcelStyle = {
  fill: { type: 'pattern'; pattern: 'solid'; fgColor: { argb: string } };
  font?: { color: { argb: string } };
};

const buildStyleLookup = (
  styles: ExcelStyle[],
): Map<string, CellExcelStyle> => {
  const map = new Map<string, CellExcelStyle>();
  for (const s of styles) {
    if (!s.interior?.color) continue;
    const argb = 'FF' + s.interior.color.replace('#', '');
    const entry: CellExcelStyle = {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb } },
    };
    if (s.font?.color) {
      entry.font = { color: { argb: 'FF' + s.font.color.replace('#', '') } };
    }
    map.set(s.id, entry);
  }
  return map;
};

const resolveExcelStyle = (
  className: string,
  lookup: Map<string, CellExcelStyle>,
): CellExcelStyle | undefined => {
  const direct = lookup.get(className);
  if (direct) return direct;
  for (const part of className.split(/\s+/)) {
    const s = lookup.get(part);
    if (s) return s;
  }
  return undefined;
};

export type ExportGridToExcelOpts = {
  sheetName: string;
  filename: string;
  omit?: string[];
  preserveColors?: boolean;
};

export async function exportGridToExcel(
  api: GridApi,
  {
    sheetName,
    filename,
    omit = [],
    preserveColors = false,
  }: ExportGridToExcelOpts,
): Promise<void> {
  const displayed: Column[] = api.getAllDisplayedColumns();
  if (!displayed.length) return;

  const isServerSide = api.getModel().getType() !== 'clientSide';

  const rows: Record<string, unknown>[] = [];
  const columnApi = api as unknown as ColumnApi;
  const gridContext = (api as any).context as any;

  let styleLookup: Map<string, CellExcelStyle> | null = null;
  if (preserveColors) {
    const styles = api.getGridOption('excelStyles') as ExcelStyle[] | undefined;
    if (styles?.length) {
      styleLookup = buildStyleLookup(styles);
    }
  }

  const rowCellStyles: Map<string, CellExcelStyle>[] = [];

  const processNode = (node: any) => {
    if (node.group || node.data == null) return;
    const obj: Record<string, unknown> = {};
    const cellStyles = new Map<string, CellExcelStyle>();

    displayed.forEach((col) => {
      const def = col.getColDef();
      const header = def.headerName ?? col.getColId();

      let value = api.getValue(col, node);

      if (styleLookup && def.cellClassRules) {
        const classParams = {
          value,
          data: node.data,
          node,
          column: col,
          colDef: def,
          api,
          columnApi,
          context: gridContext,
        };
        for (const [cls, ruleFn] of Object.entries(def.cellClassRules)) {
          if (typeof ruleFn === 'function') {
            try {
              if ((ruleFn as (p: unknown) => boolean)(classParams)) {
                const matched = resolveExcelStyle(cls, styleLookup);
                if (matched) cellStyles.set(header, matched);
                break;
              }
            } catch {
              console.warn('Error evaluating cellClassRules for class:', cls);
            }
          }
        }
      }

      if (typeof def.valueFormatter === 'function') {
        const fmtParams: ValueFormatterParams = {
          value,
          data: node.data,
          node,
          column: col,
          colDef: def as ColDef,
          api,
          columnApi,
          context: gridContext,
        };
        value = def.valueFormatter(fmtParams);
      }

      obj[header] = value ?? '';
    });

    rows.push(obj);
    rowCellStyles.push(cellStyles);
  };

  if (isServerSide) {
    api.forEachNode(processNode);
  } else {
    api.forEachNodeAfterFilterAndSort(processNode);
  }

  if (!rows.length) return;

  const headers = Object.keys(rows[0]).filter((h) => !omit.includes(h));
  if (!headers.length) return;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);

  ws.addRow(headers);
  rows.forEach((r) => ws.addRow(headers.map((h) => r[h])));

  if (styleLookup) {
    rowCellStyles.forEach((cellStyles, rowIdx) => {
      if (!cellStyles.size) return;
      const excelRow = ws.getRow(rowIdx + 2);
      headers.forEach((h, colIdx) => {
        const style = cellStyles.get(h);
        if (!style) return;
        const cell = excelRow.getCell(colIdx + 1);
        cell.fill = style.fill;
        if (style.font) {
          cell.font = { ...cell.font, ...style.font };
        }
      });
    });
  }

  ws.views = [{ state: 'frozen', ySplit: 1 }];
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  ws.getRow(1).eachCell((c) => {
    c.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '000000' },
    };
    c.font = { color: { argb: 'FFFFFF' }, bold: true };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
  });

  headers.forEach((h, i) => {
    const col = ws.getColumn(i + 1);
    const max = Math.max(
      h.length,
      ...rows.map((r) => String(r[h] ?? '').length),
    );
    col.width = Math.min(max + 2, 50);
    col.alignment = {
      wrapText: true,
      horizontal: 'left',
      vertical: 'middle',
    };
    if (isTextColumn(h)) {
      col.numFmt = '@';
      col.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
        if (rowNumber > 1) {
          cell.value = String(cell.value ?? '');
          cell.numFmt = '@';
        }
      });
    }
  });

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), filename);
}

type Opts = {
  gridApiRef: MutableRefObject<GridApi | null>;
  sheetName: string;
  filename: string;
  omit?: string[];
};

export function useExcelExport({
  gridApiRef,
  sheetName,
  filename,
  omit = [],
}: Opts) {
  const preserveColors = useFeatureFlag('Excel_Keep_Color');
  return useCallback(async () => {
    const api = gridApiRef.current;
    if (!api) return;
    await exportGridToExcel(api, {
      sheetName,
      filename,
      omit,
      preserveColors,
    });
  }, [gridApiRef, sheetName, filename, omit, preserveColors]);
}
